import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import { db, storage } from "../firebase/firebase";
import "./Ebooks.css";

export default function Ebooks() {
  // ============================================================
  // STATE
  // ============================================================
  const [loading, setLoading] = useState(true);
  const [ebooks, setEbooks] = useState([]);

  // Form
  const [titulo, setTitulo] = useState("");
  const [kiwifyUrl, setKiwifyUrl] = useState("");
  const [ordem, setOrdem] = useState(1);
  const [ativo, setAtivo] = useState(true);

  // Upload
  const [fileCapa, setFileCapa] = useState(null);
  const [previewCapa, setPreviewCapa] = useState("");

  // Edit
  const [editId, setEditId] = useState(null);
  const [capaAtualUrl, setCapaAtualUrl] = useState("");

  const isEditMode = useMemo(() => !!editId, [editId]);

  // ============================================================
  // HELPERS
  // ============================================================
  function resetForm() {
    setTitulo("");
    setKiwifyUrl("");
    setOrdem(ebooks.length + 1);
    setAtivo(true);

    setFileCapa(null);
    setPreviewCapa("");

    setEditId(null);
    setCapaAtualUrl("");
  }

  function isValidHttpUrl(url) {
    if (!url) return false;
    try {
      const u = new URL(url);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  }

  // ✅ Converte downloadURL (firebase storage) em ref(storage, fullPath)
  // (Substitui o refFromURL que não existe no seu Firebase)
  function storageRefFromDownloadUrl(url) {
    if (!url || typeof url !== "string") return null;

    // se for gs:// (alguns casos), o ref() aceita
    if (url.startsWith("gs://")) {
      return ref(storage, url);
    }

    // downloadURL padrão:
    // https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<path%2Ffile>?alt=media&token=...
    try {
      const u = new URL(url);
      const marker = "/o/";
      const idx = u.pathname.indexOf(marker);

      if (idx === -1) return null;

      const encodedPath = u.pathname.substring(idx + marker.length); // tudo após "/o/"
      const fullPath = decodeURIComponent(encodedPath); // path real no bucket

      if (!fullPath || fullPath.trim().length === 0) return null;

      return ref(storage, fullPath);
    } catch {
      return null;
    }
  }

  async function uploadCoverAndGetUrl(file) {
    const safeName = (file?.name || "capa")
      .replace(/\s+/g, "_")
      .replace(/[^\w.\-]+/g, "");

    const path = `ebooks/capas/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  }

  async function deleteCoverByUrl(url) {
    const r = storageRefFromDownloadUrl(url);
    if (!r) return;
    try {
      await deleteObject(r);
    } catch (e) {
      // se já não existir, não trava o fluxo
      console.warn("Falha ao deletar capa antiga:", e?.message || e);
    }
  }

  // ============================================================
  // LOAD LIST (Realtime)
  // ============================================================
  useEffect(() => {
    const q = query(collection(db, "ebooks"), orderBy("ordem", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEbooks(list);
        setLoading(false);

        // seta ordem padrão se não estiver editando
        setOrdem((prev) => {
          if (editId) return prev;
          const next = list.length + 1;
          return prev && prev > 0 ? prev : next;
        });
      },
      () => setLoading(false)
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Preview local da imagem
  useEffect(() => {
    if (!fileCapa) return;

    const url = URL.createObjectURL(fileCapa);
    setPreviewCapa(url);

    return () => URL.revokeObjectURL(url);
  }, [fileCapa]);

  // ============================================================
  // CRUD
  // ============================================================
  async function handleSubmit(e) {
    e.preventDefault();

    if (!titulo.trim()) {
      alert("Preencha o título do e-book.");
      return;
    }

    if (!kiwifyUrl.trim() || !isValidHttpUrl(kiwifyUrl.trim())) {
      alert("Informe um link válido (https://...) da Kiwify.");
      return;
    }

    // Em criação: capa obrigatória. Em edição: só se trocar.
    if (!isEditMode && !fileCapa) {
      alert("Faça upload da capa do e-book.");
      return;
    }

    const ordemNum = Number(ordem);
    if (!ordemNum || ordemNum < 1) {
      alert("A ordem precisa ser um número >= 1.");
      return;
    }

    try {
      // 1) Upload se necessário
      let capaUrlFinal = capaAtualUrl;

      if (fileCapa) {
        capaUrlFinal = await uploadCoverAndGetUrl(fileCapa);

        // Se estava editando e tinha uma capa antiga, remove a antiga
        if (isEditMode && capaAtualUrl) {
          await deleteCoverByUrl(capaAtualUrl);
        }
      }

      // 2) Save no Firestore
      const payload = {
        titulo: titulo.trim(),
        kiwifyUrl: kiwifyUrl.trim(),
        ordem: ordemNum,
        ativo: !!ativo,
        capaUrl: capaUrlFinal || "",
        updatedAt: serverTimestamp(),
      };

      if (isEditMode) {
        await updateDoc(doc(db, "ebooks", editId), payload);
      } else {
        await addDoc(collection(db, "ebooks"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      alert(isEditMode ? "E-book atualizado ✅" : "E-book cadastrado ✅");
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar. Verifique o console.");
    }
  }

  function handleEdit(item) {
    setEditId(item.id);

    setTitulo(item.titulo || "");
    setKiwifyUrl(item.kiwifyUrl || "");
    setOrdem(item.ordem || 1);
    setAtivo(item.ativo !== false);

    setCapaAtualUrl(item.capaUrl || "");
    setPreviewCapa(item.capaUrl || "");
    setFileCapa(null);

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDelete(item) {
    const ok = window.confirm(
      `Excluir o e-book "${item.titulo || "Sem título"}"?`
    );
    if (!ok) return;

    try {
      // apaga capa do storage, se existir
      if (item.capaUrl) {
        await deleteCoverByUrl(item.capaUrl);
      }

      await deleteDoc(doc(db, "ebooks", item.id));

      // se estava editando esse mesmo
      if (editId === item.id) {
        resetForm();
      }

      alert("E-book excluído ✅");
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir. Verifique o console.");
    }
  }

  // ============================================================
  // UI
  // ============================================================
  return (
    <div className="ebooks-page">
      <div className="ebooks-header">
        <h1>Ebooks (App)</h1>
        <p>
          Cadastre capas via upload e defina o link da Kiwify do botão “Começar
          Jornada”.
        </p>
      </div>

      {/* FORM */}
      <div className="ebooks-card">
        <h2>{isEditMode ? "Editar e-book" : "Cadastrar e-book"}</h2>

        <form onSubmit={handleSubmit} className="ebooks-form">
          <div className="form-row">
            <div className="form-col">
              <label>Título</label>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: A Arte da Conexão Verdadeira"
              />
            </div>

            <div className="form-col">
              <label>Ordem (posição no app)</label>
              <input
                type="number"
                min="1"
                value={ordem}
                onChange={(e) => setOrdem(e.target.value)}
                placeholder="1"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label>Link da Kiwify (botão)</label>
              <input
                type="url"
                value={kiwifyUrl}
                onChange={(e) => setKiwifyUrl(e.target.value)}
                placeholder="https://kiwify.com.br/..."
              />
            </div>

            <div className="form-col form-switch">
              <label>Ativo no app</label>
              <div className="switch-line">
                <input
                  type="checkbox"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                />
                <span>{ativo ? "Sim" : "Não"}</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label>Capa (upload)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFileCapa(e.target.files?.[0] || null)}
              />
              <small className="hint">
                {isEditMode
                  ? "Se você enviar uma nova capa, a antiga será removida do Storage."
                  : "Obrigatório no cadastro."}
              </small>
            </div>

            <div className="form-col">
              <label>Preview</label>
              <div className="cover-preview">
                {previewCapa ? (
                  <img src={previewCapa} alt="Preview da capa" />
                ) : (
                  <div className="cover-placeholder">Sem capa</div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {isEditMode ? "Salvar alterações" : "Cadastrar"}
            </button>

            {isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={resetForm}
              >
                Cancelar edição
              </button>
            )}
          </div>
        </form>
      </div>

      {/* LIST */}
      <div className="ebooks-card">
        <h2>Lista cadastrada</h2>

        {loading ? (
          <div className="loading">Carregando…</div>
        ) : ebooks.length === 0 ? (
          <div className="empty">Nenhum e-book cadastrado ainda.</div>
        ) : (
          <div className="ebooks-grid">
            {ebooks.map((item) => (
              <div key={item.id} className="ebook-item">
                <div className="ebook-cover">
                  {item.capaUrl ? (
                    <img src={item.capaUrl} alt={item.titulo || "Capa"} />
                  ) : (
                    <div className="cover-placeholder">Sem capa</div>
                  )}
                </div>

                <div className="ebook-meta">
                  <div className="ebook-title">
                    {item.titulo || "Sem título"}
                  </div>

                  <div className="ebook-sub">
                    Ordem: <b>{item.ordem ?? "—"}</b> •{" "}
                    {item.ativo === false ? "Inativo" : "Ativo"}
                  </div>

                  <a
                    className="ebook-link"
                    href={item.kiwifyUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (!item.kiwifyUrl) e.preventDefault();
                    }}
                  >
                    Abrir link da Kiwify
                  </a>

                  <div className="ebook-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleEdit(item)}
                    >
                      Editar
                    </button>

                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => handleDelete(item)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>

                <div className="ebook-btn">
                  <a
                    href={item.kiwifyUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-kiwify"
                    onClick={(e) => {
                      if (!item.kiwifyUrl) e.preventDefault();
                    }}
                  >
                    Começar Jornada
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
